import { type ForwardRefExoticComponent, forwardRef } from 'react';

import { SwitchHandle } from './switch-handle';
import { SwitchInput } from './switch-input';
import { SwitchRoot } from './switch-root';
import { SwitchTrack } from './switch-track';
import type { SwitchPropsInterface } from './types/switch.types';

/**
 * A switch is on/off control built upon a simple checkbox input.
 *
 * @since 6.7.0
 */

export const Switch = forwardRef<HTMLDivElement, SwitchPropsInterface>(
  (props: SwitchPropsInterface, forwardedRef) => {
    const { checked, className, controlId, css, defaultChecked, disabled, size, ...rest } = props;

    return (
      <Switch.Root
        disabled={disabled}
        className={className}
        controlId={controlId}
        css={css}
        ref={forwardedRef}
        size={size}
        {...('checked' in props ? { checked } : {})}
        {...('defaultChecked' in props ? { defaultChecked } : {})}
      >
        <Switch.Input {...rest} />
        <Switch.Track>
          <Switch.Handle />
        </Switch.Track>
      </Switch.Root>
    );
  },
) as ForwardRefExoticComponent<SwitchPropsInterface> & {
  Root: typeof SwitchRoot;
  Handle: typeof SwitchHandle;
  Input: typeof SwitchInput;
  Track: typeof SwitchTrack;
};

Switch.displayName = 'Switch';

Switch.Root = SwitchRoot;
Switch.Handle = SwitchHandle;
Switch.Input = SwitchInput;
Switch.Track = SwitchTrack;
